import { FadeUp } from "@/components/Reveal";
import { LOGO_URL } from "@/data/content";

export const VinylRitual = () => {
  return (
    <section
      id="vinyl"
      data-testid="vinyl-section"
      className="section-pad relative bg-[#0a0a0a]"
    >
      <div className="max-w-[1200px] mx-auto grid md:grid-cols-12 gap-12 md:gap-20 items-center">
        {/* Visual: spinning record disc + 33 RPM logo label (both spin together like a real vinyl) */}
        <FadeUp className="md:col-span-5">
          <div className="relative mx-auto aspect-square max-w-[440px]">
            {/* Outer spinning record — disc grooves AND the center logo label are inside the same rotating element */}
            <div
              className="absolute inset-0 rounded-full"
              style={{
                background:
                  "radial-gradient(circle at center, #0a0a0a 0%, #0a0a0a 18%, #1a1a1a 19%, #0d0d0d 22%, #1a1a1a 26%, #0d0d0d 30%, #1a1a1a 34%, #0d0d0d 38%, #1a1a1a 42%, #0d0d0d 46%, #1a1a1a 50%, #0d0d0d 54%, #1a1a1a 58%, #0d0d0d 62%, #1a1a1a 66%, #0d0d0d 70%, #1a1a1a 74%, #0d0d0d 78%, #1a1a1a 82%, #0d0d0d 86%, #1a1a1a 90%, #0a0a0a 100%)",
                animation: "spin 16s linear infinite",
                boxShadow:
                  "0 0 60px rgba(212,175,55,0.08), inset 0 0 30px rgba(0,0,0,0.6)",
              }}
              aria-hidden="true"
            >
              {/* Center logo label — inside the same rotating div so it spins with the disc */}
              <div
                className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[36%] h-[36%] rounded-full overflow-hidden border border-[#D4AF37]/60 flex items-center justify-center"
                style={{
                  boxShadow:
                    "0 0 18px rgba(212,175,55,0.18), inset 0 0 10px rgba(0,0,0,0.6)",
                  background: "#0a0a0a",
                }}
              >
                <img
                  src={LOGO_URL}
                  alt="33 RPM"
                  className="w-full h-full object-cover"
                  data-testid="vinyl-center-logo"
                />
                {/* Spindle hole */}
                <span
                  className="absolute w-2 h-2 rounded-full bg-[#0a0a0a] z-10"
                  style={{ boxShadow: "inset 0 0 2px rgba(255,255,255,0.25)" }}
                  aria-hidden="true"
                />
              </div>
            </div>
            {/* Subtle non-spinning sheen overlay */}
            <div
              className="absolute inset-0 rounded-full pointer-events-none"
              style={{
                background:
                  "linear-gradient(135deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0) 40%, rgba(255,255,255,0) 60%, rgba(255,255,255,0.04) 100%)",
              }}
              aria-hidden="true"
            />
          </div>
          <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
        </FadeUp>

        <FadeUp delay={0.1} className="md:col-span-7">
          <div className="eyebrow-line">Bring a Record</div>
          <h2
            className="font-serif-display text-[#FDFBF7] mt-6 leading-[1.05]"
            style={{ fontSize: "clamp(36px, 5vw, 56px)" }}
          >
            The needle drops{" "}
            <span className="italic text-[#D4AF37]">for every guest.</span>
          </h2>
          <p className="mt-8 text-[#FDFBF7]/85 text-lg leading-[1.75] max-w-[58ch] font-light">
            Each invited guest will choose a record to be played during the
            evening. If you have a favorite — something with meaning, something
            you've been carrying, something the room needs to hear — bring it.
          </p>
          <p className="mt-5 text-[#A3A3A3] leading-[1.75] max-w-[58ch]">
            I'll keep a backup library on hand, but your selection sets the
            soundtrack to your own dinner. In the spirit of true vinyl culture:
            no streaming, no skipping, no shuffle. The album plays as the
            artist arranged it.
          </p>

          <ul className="mt-10 grid sm:grid-cols-2 gap-5 max-w-[56ch]">
            {[
              ["Format", "12-inch vinyl LPs only"],
              ["Genre", "Anything — jazz, soul, R&B, ambient, hip-hop, world"],
              ["Condition", "Cared-for, please. We'll handle it gently."],
              ["No vinyl?", "I'll choose one in your name."],
            ].map(([k, v]) => (
              <li key={k}>
                <div className="overline text-[10px]">{k}</div>
                <div className="mt-2 text-[#FDFBF7]/90 text-[15px] leading-snug">
                  {v}
                </div>
              </li>
            ))}
          </ul>
        </FadeUp>
      </div>
    </section>
  );
};
